/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { post } from '@/Utils/API';
import { CHANGED_COLUMN_POSITION, MODAL_CREATE_ACCOUNT, URL_DATASET } from '@/Utils/constants';
import { getFileUuidFromPath } from '@/Utils/getFileUuidFromPath';
import { ROW_NUMBER_COLUMN_ID } from '@/Utils/gridConstants';
import { hasNoWritePermission } from '@/helpers/spreadsheetHelper';
import useOnClickOutside from '@/hooks/useClickOutside';
import { useShowPreviewModeUpsellModal } from '@/hooks/useShowPreviewModeUpsellModal';
import { datasetSliceName, spreadsheetSliceName, userDataSliceName } from '@/redux/constants';
import { showModal } from '@/redux/reducers/modalsSlice';
import {
  setColumnDefs,
  setIsRenamePopoverOpen,
  setRenameColumnData,
} from '@/redux/reducers/spreadsheetSlice';
import clsx from 'clsx';
import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import CustomToggleSwitch from '../CustomToggleSwitch/CustomToggleSwitch';
import ShowToast from '../Toast/showToastTemplate';
import { Button } from '../UI/Button/Button';
import CheckboxInput from '../UI/Form/CheckboxInput/CheckboxInput';
import Icon from '../UI/Icon/Icon';
import Tooltip from '../UI/Tooltip/Tooltip';

const CustomColumnManagerItem = ({
  col,
  agGrid,
  handleColumnCheckbox,
  handleDeleteColumn,
  handleColumnSelection,
  selectedColumn = [],
  colIndex,
  handleRightMouseClick,
  isMobileVersion = false,
}) => {
  const isColumnSelected = selectedColumn?.some((el) => el.colId === col.colId);

  return (
    <div
      data-cy={`column-manager-row-${col.headerName}`}
      className={clsx(
        'flex justify-start items-center p-1 h-8 hover:bg-midnight-100 hover:bg-opacity-16 rounded text-sm whitespace-nowrap cursor-pointer',
        isColumnSelected && 'bg-midnight-100 bg-opacity-16'
      )}
    >
      {isMobileVersion ? (
        <CustomToggleSwitch
          onChange={(e) => handleColumnCheckbox(e, col, colIndex)}
          isChecked={!col?.hide}
          switcher='SimpleSwitcher'
          wrapperClassName='scale-75'
        />
      ) : (
        <div className='flex items-center justify-start'>
          <Icon
            name='dots-six'
            color='#707070'
            disabled={col?.colId === ROW_NUMBER_COLUMN_ID}
            className={clsx(
              'mr-2',
              col?.colId === ROW_NUMBER_COLUMN_ID ? 'cursor-not-allowed' : ''
            )}
          />
          <Tooltip
            text={!col?.hide ? 'Visible' : 'Hidden'}
            side='right'
            sideOffset={-4}
            className='py-0'
          >
            <CheckboxInput
              onChange={(e) => handleColumnCheckbox(e, col, colIndex)}
              checked={!col?.hide}
              dataCy={`column-manager-row-checkbox-${col.headerName}`}
              className='mt-2'
            />
          </Tooltip>
        </div>
      )}
      <div
        role='button'
        tabIndex={0}
        onKeyDown={() => null}
        className='flex items-center w-full'
        onClick={(e) => !isMobileVersion && handleColumnSelection(e, col, colIndex)}
        onContextMenu={(e) => !isMobileVersion && handleRightMouseClick(e, col)}
        data-cy={`column-manager-row-column-${col.headerName}`}
        data-cy-selected={isColumnSelected}
      >
        <span
          className={clsx(
            'inline items-center ml-2 mt-1 max-w-[650px] truncate text-ui',
            isMobileVersion && '!w-60'
          )}
        >
          {col?.headerName}
        </span>
      </div>

      {!isMobileVersion && (
        <div className='relative flex items-center justify-start gap-1'>
          <ColumnProperties column={col} handleDeleteColumn={handleDeleteColumn} agGrid={agGrid} />
        </div>
      )}
    </div>
  );
};

export default CustomColumnManagerItem;

const ColumnProperties = ({ column, handleDeleteColumn, agGrid }) => {
  const dispatch = useDispatch();
  const showPreviewModeUpsellModal = useShowPreviewModeUpsellModal();
  const { isPreviewMode } = useSelector((state) => state[datasetSliceName]);

  const { user, isViewerAnalyst } = useSelector((state) => state[userDataSliceName]);
  const { columnDefs } = useSelector((state) => state[spreadsheetSliceName]);
  const { currentFile } = useSelector((state) => state[datasetSliceName]);

  const revenueBaseAccess =
    !!user?.userProperties?.revenueBaseAccess && hasNoWritePermission(currentFile);

  const moveAndEnsureColumnVisible = async (columnId, targetIndex) => {
    agGrid.api.moveColumn(columnId, targetIndex);

    await requestAnimationFrameAsync();
    await requestAnimationFrameAsync();

    agGrid.api.ensureColumnVisible(columnId);
  };

  const onColumnPositionChange = async (columnId, fromIndex, toIndex) => {
    const uuid = getFileUuidFromPath();
    const data = {
      col_id: columnId,
      from_index: fromIndex,
      to_index: toIndex,
    };

    try {
      await post(`${URL_DATASET}/${uuid}/${CHANGED_COLUMN_POSITION}`, data);
    } catch (error) {
      console.error('Error column position change:', error);
    }
  };
  function moveColIdToPosition(array, colId, position = 'start') {
    if (!Array.isArray(array)) return;
    if (colId === ROW_NUMBER_COLUMN_ID) return;
    const newArray = [...array];
    const index = newArray.findIndex((item) => item.colId === colId);

    if (index !== -1) {
      const [movedItem] = newArray.splice(index, 1);
      if (position === 'start') {
        newArray.splice(1, 0, movedItem);
      } else if (position === 'end') {
        newArray.push(movedItem);
      }
    }
    return newArray;
  }

  const scrollToFirstColumn = async () => {
    if (!agGrid || !column || !column.colId) return;

    const columnState = agGrid.api.getColumnState();
    const fromIndex = columnState.findIndex((e) => e.colId === column.colId);

    dispatch(setColumnDefs(moveColIdToPosition(columnDefs, column.colId, 'start')));
    await moveAndEnsureColumnVisible(column.colId, 1);
    await onColumnPositionChange(column.colId, fromIndex, 1);
  };

  const scrollToLastColumn = async () => {
    if (!agGrid || !column || !column.colId) return;

    const columnState = agGrid.api.getColumnState();
    const fromIndex = columnState.findIndex((e) => e.colId === column.colId);
    const toIndex = columnState.length - 1;

    dispatch(setColumnDefs(moveColIdToPosition(columnDefs, column.colId, 'end')));
    await moveAndEnsureColumnVisible(column.colId, toIndex);
    await onColumnPositionChange(column.colId, fromIndex, toIndex);
  };

  const requestAnimationFrameAsync = () => {
    return new Promise((resolve) => {
      requestAnimationFrame(() => resolve());
    });
  };

  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const listRef = useRef(null);

  const listConfig = [
    {
      name: 'Scroll to Column',
      icon: 'arrows-horizontal',
      disabled: false,
      action: () => {
        if (user?.is_anonymous) {
          return dispatch(showModal({ name: MODAL_CREATE_ACCOUNT }));
        }
        if (column.hide)
          return ShowToast({
            type: 'error',
            text: 'Column is hidden',
            fileAndFunction: '__FILE_AND_FUNCTION_NAME__',
          });
        agGrid.api.ensureColumnVisible(column.colId);
        setIsOpen(false);
      },
    },
    {
      name: 'Move to Start',
      icon: 'arrow-line-up',
      disabled: column?.colId === ROW_NUMBER_COLUMN_ID,
      action: () => {
        if (user?.is_anonymous) {
          return dispatch(showModal({ name: MODAL_CREATE_ACCOUNT }));
        }
        if (isPreviewMode) {
          return showPreviewModeUpsellModal();
        }
        scrollToFirstColumn();
        setIsOpen(false);
      },
    },
    {
      name: 'Move to End',
      icon: 'arrow-line-down',
      disabled: column?.colId === ROW_NUMBER_COLUMN_ID,
      action: () => {
        if (user?.is_anonymous) {
          return dispatch(showModal({ name: MODAL_CREATE_ACCOUNT }));
        }
        if (isPreviewMode) {
          return showPreviewModeUpsellModal();
        }
        scrollToLastColumn();
        setIsOpen(false);
      },
    },
    {
      name: 'Column Stats',
      disabled: false,
      icon: 'info',
      action: () => {
        const ensureColumnVisible = (colId) => {
          return new Promise((resolve, reject) => {
            if (user?.is_anonymous) {
              return dispatch(showModal({ name: MODAL_CREATE_ACCOUNT }));
            }
            if (isPreviewMode) {
              return showPreviewModeUpsellModal();
            }
            try {
              agGrid.api.ensureColumnVisible(colId);
              dispatch(
                setRenameColumnData({ colId, columnName: column.headerName, hide: column.hide })
              );
              dispatch(setIsRenamePopoverOpen(true));
              resolve();
            } catch (error) {
              reject(error);
            }
          });
        };

        ensureColumnVisible(column.colId);
      },
    },
  ];

  const onDeleteColumnClick = (column) => {
    if (column?.colId === ROW_NUMBER_COLUMN_ID) return null;
    if (user?.is_anonymous) {
      return dispatch(showModal({ name: MODAL_CREATE_ACCOUNT }));
    }
    handleDeleteColumn(column);
  };

  useOnClickOutside(listRef, () => setIsOpen(false));

  useLayoutEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      setPosition({ top: buttonRect.bottom - 5, left: buttonRect.left + 12 });
    }
  }, [isOpen]);

  const viewOnlyActions = ['Scroll to Column', 'Column Stats'];
  const menuConfig =
    isViewerAnalyst || revenueBaseAccess
      ? listConfig.filter((i) => viewOnlyActions.includes(i.name))
      : listConfig;

  return (
    <>
      <Button
        ref={buttonRef}
        onClick={() => setIsOpen((prev) => !prev)}
        variant='ghost'
        color='shadow'
        className='!px-0 !w-6 !h-6'
        dataCy={`column-manager-option-list-${column.headerName}`}
      >
        <Icon name='dots-three-vertical' color='#151515' />
      </Button>
      {isOpen &&
        createPortal(
          <div
            ref={listRef}
            className='absolute z-50  bg-white border rounded w-[250px] p-1  !shadow-customColumnManager'
            style={{
              top: position.top,
              left: position.left,
            }}
          >
            {menuConfig.map((el) => (
              <ColumnProperty key={el.name} property={el} />
            ))}
            {isViewerAnalyst || revenueBaseAccess ? null : (
              <>
                <hr className='my-2 bg-shadow-200' />
                <div
                  className={clsx(
                    'flex !justify-start rounded w-full p-2 hover:bg-shadow-100 hover:bg-opacity-40 text-sm',
                    column?.colId === ROW_NUMBER_COLUMN_ID ? 'cursor-not-allowed' : 'cursor-pointer'
                  )}
                  data-cy={`column-manager-row-delete-button-${column.headerName}`}
                  onClick={() => onDeleteColumnClick(column)}
                >
                  <Icon name='trash' />
                  <div className='pl-2'>Delete</div>
                </div>
              </>
            )}
          </div>,
          document.body
        )}
    </>
  );
};

const ColumnProperty = ({ property }) => {
  const { name, icon, action, disabled } = property || {};

  return (
    <div
      onClick={disabled ? () => null : action}
      className={clsx(
        'flex !justify-start w-full p-2 cursor-pointer hover:bg-shadow-100  hover:bg-opacity-40 rounded text-sm',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer'
      )}
    >
      <Icon name={icon} />
      <div className='pl-2'>{name}</div>
    </div>
  );
};
